import supabase from '../supabaseClient';
import dayjs from 'dayjs';

/**
 * Fetches all classes accessible to a teacher (regular classes + substitute assignments)
 * @param {string} teacherId - The teacher's ID
 * @param {string} selectFields - Fields to select (default: 'id, name, shift, start_time, end_time')
 * @param {string} date - Optional specific date to check for substitute assignments (YYYY-MM-DD)
 * @returns {Promise<Array>} Array of classes with metadata about substitute status
 */
export const getTeacherAccessibleClasses = async (teacherId, selectFields = 'id, name, shift, start_time, end_time', date = null) => {
  try {
    const classes = [];
    
    // 1. Get regular classes taught by this teacher
    const { data: regularClasses, error: regularError } = await supabase
      .from('classes')
      .select(selectFields)
      .eq('teacher_id', teacherId);
    
    if (regularError) throw regularError;
    
    // Add regular classes with metadata
    if (regularClasses) {
      classes.push(...regularClasses.map(cls => ({
        ...cls,
        isSubstitute: false,
        accessType: 'regular'
      })));
    }

    // 2. Get substitute assignments for this teacher
    const today = date ? dayjs(date) : dayjs();
    const startDate = today.format('YYYY-MM-DD');
    const endDate = today.add(30, 'day').format('YYYY-MM-DD'); // Next 30 days
    
    const { data: substituteAssignments, error: subError } = await supabase
      .from('substitute_assignments')
      .select(`
        id,
        start_date,
        end_date,
        status,
        reason,
        original_teacher_id,
        classes:class_id (${selectFields})
      `)
      .eq('substitute_teacher_id', teacherId)
      .eq('status', 'pending') // Only pending assignments give access
      .lte('start_date', endDate)
      .gte('end_date', startDate);
    
    if (subError) throw subError;
    
    // Add substitute classes with metadata
    if (substituteAssignments) {
      const substituteClasses = substituteAssignments
        .filter(assignment => assignment.classes) // Ensure class data exists
        .map(assignment => ({
          ...assignment.classes,
          isSubstitute: true,
          accessType: 'substitute',
          assignmentId: assignment.id,
          substituteStartDate: assignment.start_date,
          substituteEndDate: assignment.end_date,
          substituteReason: assignment.reason,
          originalTeacherId: assignment.original_teacher_id
        }));
      
      classes.push(...substituteClasses);
    }

    // 3. Remove duplicates (in case a teacher is both regular and substitute for same class)
    const uniqueClasses = [];
    const seenClassIds = new Set();
    
    for (const cls of classes) {
      if (!seenClassIds.has(cls.id)) {
        uniqueClasses.push(cls);
        seenClassIds.add(cls.id);
      } else {
        // If duplicate, prefer regular access over substitute access
        const existingIndex = uniqueClasses.findIndex(existing => existing.id === cls.id);
        if (existingIndex !== -1 && uniqueClasses[existingIndex].isSubstitute && !cls.isSubstitute) {
          uniqueClasses[existingIndex] = cls;
        }
      }
    }

    return uniqueClasses;
  } catch (error) {
    console.error('Error fetching teacher accessible classes:', error);
    throw error;
  }
};

/**
 * Checks if a teacher has access to a specific class on a specific date
 * @param {string} teacherId - The teacher's ID
 * @param {string} classId - The class ID
 * @param {string} date - Date in YYYY-MM-DD format (optional, defaults to today)
 * @returns {Promise<Object>} Access information
 */
export const getTeacherClassAccess = async (teacherId, classId, date = null) => {
  try {
    const checkDate = date || dayjs().format('YYYY-MM-DD');
    
    // Check if regular teacher
    const { data: classData, error: classError } = await supabase
      .from('classes')
      .select('id, name, teacher_id, shift')
      .eq('id', classId)
      .single();
    
    if (classError) throw classError;
    
    if (classData.teacher_id === teacherId) {
      return {
        hasAccess: true,
        accessType: 'regular',
        isSubstitute: false,
        className: classData.name
      };
    }
    
    // Check if substitute teacher for this date
    const { data: substituteAssignment, error: subError } = await supabase
      .from('substitute_assignments')
      .select('id, start_date, end_date, reason, status')
      .eq('substitute_teacher_id', teacherId)
      .eq('class_id', classId)
      .eq('status', 'pending')
      .lte('start_date', checkDate)
      .gte('end_date', checkDate)
      .maybeSingle();
    
    if (subError) throw subError;
    
    if (substituteAssignment) {
      return {
        hasAccess: true,
        accessType: 'substitute',
        isSubstitute: true,
        className: classData.name,
        assignmentId: substituteAssignment.id,
        substituteReason: substituteAssignment.reason,
        substituteStartDate: substituteAssignment.start_date,
        substituteEndDate: substituteAssignment.end_date
      };
    }
    
    return {
      hasAccess: false,
      accessType: null,
      isSubstitute: false,
      className: classData.name
    };
  } catch (error) {
    console.error('Error checking teacher class access:', error);
    return {
      hasAccess: false,
      accessType: null,
      isSubstitute: false,
      reason: 'Error checking access'
    };
  }
};

/**
 * Gets the display name for a class including substitute context
 * @param {Object} classObj - Class object from getTeacherAccessibleClasses
 * @returns {string} Display name
 */
export const getClassDisplayName = (classObj) => {
  if (classObj.isSubstitute) {
    return `${classObj.name} (Substitute)`;
  }
  return classObj.name;
};

/**
 * Legacy function for backward compatibility - gets classes for a teacher
 * This replaces the old pattern of .eq('teacher_id', teacherId)
 * @param {string} teacherId - The teacher's ID
 * @param {string} selectFields - Fields to select
 * @returns {Promise<Array>} Array of classes
 */
export const getTeacherClasses = async (teacherId, selectFields = '*') => {
  return await getTeacherAccessibleClasses(teacherId, selectFields);
};
